import { MAX_WHOLE_DIGITS, typedValue, wasLine, zerosCheck } from "../app/edit-model.js";
import { el } from "../lib/dom.js";
import { formatMoney } from "../lib/format.js";
import { icon } from "../lib/icon.js";
import { sgdWords } from "../lib/money-words.js";
import { frame } from "./sheet.js";

/**
 * DCTA-170 (F35, F37; docs/design/v3/03-edit-in-place.md §0.2, mock-up 21-edit.html `amount`, `amount-refused`,
 * `dependants`) — the two sheets the card's edit state opens. Every word is §F.2 row 22's; the figure the customer types
 * stays theirs until the bank answers, and nothing here works out a figure the bank would sign.
 */

export interface AmountSheetModel {
  /** 22g: "{Who} · from {account}". */
  readonly who: string;
  /** The figure as signed now ("80.00"): "Was …", or the zeros check at ten times or more. */
  readonly previous: string;
  /** 22k: what the account has, when the payload signs it. */
  readonly avail?: string | undefined;
}

export interface AmountSheetHandlers {
  /** "Change to {amount}" with a figure above zero, two decimals at most ("800.00"). */
  onChange(value: string): void;
  onCancel(): void;
  /** Under a cap's refusal (21s): the Limits page. */
  onLimits?: (() => void) | undefined;
}

let seq = 0;

/**
 * 22g–22n: the amount sheet over the decimal keypad. A real field (`inputmode="decimal"`, SGD fixed beside it, two decimals
 * at most), the figure in words as it is typed, "Was …" or the zeros check, what the account has, the bank's refusal under
 * the figure (`role=alert` inside the sheet only), and "Change to {amount}" — which rests after a refusal until the figure
 * changes, because the same figure would be refused again.
 */
export class AmountSheet {
  readonly element: HTMLElement;
  readonly input: HTMLInputElement;
  readonly #words: HTMLElement;
  readonly #was: HTMLElement;
  readonly #zeros: HTMLElement;
  readonly #err: HTMLElement;
  readonly #errText: HTMLElement;
  readonly #limits: HTMLButtonElement;
  readonly #go: HTMLButtonElement;
  readonly #goLabel: HTMLElement;
  readonly #previous: string;
  #busy = false;
  /** What the line under the figure says: 22n (nothing typed), or the bank's refusal of one figure — "Change to" rests until it is another. */
  #error: { readonly kind: "empty" } | { readonly kind: "refused"; readonly at: string | undefined } | undefined;

  constructor(m: AmountSheetModel, h: AmountSheetHandlers) {
    seq += 1;
    const ids = { words: `am-w-${String(seq)}`, zeros: `am-x-${String(seq)}`, err: `am-e-${String(seq)}` };
    this.#previous = m.previous;
    this.input = el("input", {
      class: "n",
      type: "text",
      inputMode: "decimal",
      autocomplete: "off",
      placeholder: "0.00",
      aria: { label: "Amount in Singapore dollars", describedby: `${ids.words} ${ids.zeros} ${ids.err}` },
    });
    this.input.setAttribute("enterkeyhint", "done");
    this.#words = el("p", { class: "words", id: ids.words });
    this.#was = el("p", { class: "was" });
    this.#zeros = el("p", { class: "zeros", id: ids.zeros, hidden: true });
    this.#errText = el("span", { class: "am" });
    this.#limits = el("button", { class: "raw lim", type: "button", hidden: true }, ["See my limits"]);
    this.#limits.addEventListener("click", () => h.onLimits?.());
    this.#err = el("div", { class: "aerr", id: ids.err, role: "alert", hidden: true, tabIndex: -1 }, [icon("i-alert"), el("div", {}, [this.#errText, " ", this.#limits])]);
    this.#goLabel = el("span", { class: "lbl" });
    this.#go = el("button", { class: "raw btn go", type: "button" }, [this.#goLabel]);
    const cancel = el("button", { class: "raw btn link", type: "button" }, ["Cancel"]);
    cancel.addEventListener("click", () => h.onCancel());

    const submit = (): void => {
      if (this.#busy || this.#go.getAttribute("aria-disabled") === "true") return;
      const value = typedValue(this.input.value);
      if (value === undefined) {
        // 22n: nothing typed, or zero.
        this.#error = { kind: "empty" };
        this.#showError("Enter an amount above S$0.00.", false);
        return;
      }
      h.onChange(value);
    };
    this.#go.addEventListener("click", submit);
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        submit();
      }
    });
    this.input.addEventListener("input", () => {
      // SGD is fixed: digits, one point, two decimals at most, twelve whole digits at most (commas as typed are kept out).
      const clean = this.input.value.replace(/[^\d.]/g, "");
      const [typed = "", ...rest] = clean.split(".");
      const whole = typed.slice(0, MAX_WHOLE_DIGITS);
      const next = rest.length === 0 ? whole : `${whole}.${rest.join("").slice(0, 2)}`;
      if (next !== this.input.value) this.input.value = next;
      this.#paint();
    });

    const amount = el("div", { class: "amtin" }, [el("label", { class: "v" }, [el("span", { class: "cur", aria: { hidden: "true" } }, ["S$"]), this.input])]);
    const sheet = frame("Change the amount", el("span", { class: "top-none", aria: { hidden: "true" } }), [
      el("p", { class: "who" }, [m.who]),
      amount,
      this.#words,
      this.#was,
      this.#zeros,
      this.#err,
      ...(m.avail === undefined ? [] : [el("p", { class: "avail" }, [m.avail])]),
      el("div", { class: "acts" }, [this.#go, cancel]),
    ]);
    sheet.classList.add("amts");
    this.element = sheet;
    this.#paint();
  }

  /** "Checking" with a spinner while the bank answers (22m); the sheet stays as it is. */
  busy(on: boolean): void {
    this.#busy = on;
    this.input.readOnly = on;
    this.#paint();
  }

  /** The bank refused the figure (22p): its sentence under the figure, focus to it, the figure kept for correcting. */
  refused(message: string, limits: boolean): void {
    this.#busy = false;
    this.input.readOnly = false;
    this.#error = { kind: "refused", at: typedValue(this.input.value) };
    this.#showError(message, limits);
    this.#paint();
    this.#err.focus({ preventScroll: true });
  }

  focusField(): void {
    this.input.focus({ preventScroll: true });
  }

  #showError(message: string, limits: boolean): void {
    this.#errText.textContent = message;
    this.#limits.hidden = !limits;
    this.#err.hidden = false;
  }

  #paint(): void {
    const value = typedValue(this.input.value);
    // The field is as wide as the figure in it, so the figure stays centred under the title (the placeholder's width when empty).
    this.input.style.width = `${String(Math.max(this.input.value.length, this.input.placeholder.length) + 0.5)}ch`;
    this.#words.textContent = sgdWords(this.input.value);
    const zeros = value === undefined ? undefined : zerosCheck(this.#previous, value);
    this.#zeros.replaceChildren(...(zeros === undefined ? [] : [el("b", {}, [zeros.lead]), ` ${zeros.rest}`]));
    this.#zeros.hidden = zeros === undefined;
    this.#was.textContent = wasLine(this.#previous);
    this.#was.hidden = zeros !== undefined;
    // A figure typed clears 22n; another figure clears the bank's refusal of the old one, and "Change to" wakes.
    const e = this.#error;
    if ((e?.kind === "empty" && value !== undefined) || (e?.kind === "refused" && value !== e.at)) {
      this.#error = undefined;
      this.#err.hidden = true;
    }
    const stuck = this.#error?.kind === "refused" && value === this.#error.at;
    this.#go.setAttribute("aria-disabled", stuck ? "true" : "false");
    this.#go.setAttribute("aria-busy", this.#busy ? "true" : "false");
    this.#goLabel.replaceChildren(
      ...(this.#busy
        ? [icon("i-spin", "spin"), " Checking"]
        : value === undefined
          ? ["Change to"]
          : ["Change to ", el("span", { class: "money" }, [formatMoney(value, "SGD")])]),
    );
  }
}

export interface DependantsChoice {
  readonly label: string;
  readonly kind: "btn" | "ghost" | "link";
  readonly onSelect: () => void;
}

/** 22q–22r: the dependants sheet — the question, the bank's sentence (`preview.words`), two or three buttons. */
export function dependantsSheet(title: string, words: string, choices: readonly DependantsChoice[]): HTMLElement {
  const sheet = frame(title, el("span", { class: "top-none", aria: { hidden: "true" } }), [
    ...(words === "" ? [] : [el("p", { class: "dw" }, [words])]),
    el(
      "div",
      { class: "acts" },
      choices.map((c) => {
        const b = el("button", { class: `raw btn${c.kind === "btn" ? "" : ` ${c.kind}`}`, type: "button" }, [c.label]);
        b.addEventListener("click", c.onSelect);
        return b;
      }),
    ),
  ]);
  sheet.classList.add("dep");
  return sheet;
}

/** One of the customer's accounts in the account sheet (22o). */
export interface AccountChoice {
  readonly accountId: string;
  /** "Joint Account ···2280". */
  readonly label: string;
  /** What it has available, the bank's read, formatted ("S$14,300.00"). */
  readonly available: string;
  /** The account the step comes from now: pressed, with the check. */
  readonly current: boolean;
}

/**
 * F39 (22o; mock-up 21-edit `source`): the account sheet — "Pay from", each of the customer's own accounts with what it has
 * available, the one it comes from now pressed and checked, and Cancel. A pick is the bank's (`set_source`); its refusal
 * stays in the sheet, in the bank's words (`role=alert`, as 22p under the amount), and that account rests until another
 * is picked — the same pick would be refused again (README R115: the mock-up draws no refusal here).
 */
export class AccountSheet {
  readonly element: HTMLElement;
  readonly #rows: readonly HTMLButtonElement[];
  readonly #err: HTMLElement;
  #busy = false;

  constructor(choices: readonly AccountChoice[], h: { onPick(accountId: string): void; onCancel(): void }) {
    this.#rows = choices.map((c) => {
      const b = el("button", { class: "raw acc", type: "button", aria: { pressed: c.current ? "true" : "false" } }, [
        el("span", {}, [el("b", {}, [c.label]), el("small", {}, [el("span", { class: "money" }, [c.available]), " available"])]),
        ...(c.current ? [icon("i-check")] : []),
      ]);
      b.dataset["account"] = c.accountId;
      b.addEventListener("click", () => {
        if (this.#busy || b.getAttribute("aria-disabled") === "true") return;
        h.onPick(c.accountId);
      });
      return b;
    });
    this.#err = el("div", { class: "serr", role: "alert", hidden: true, tabIndex: -1 });
    const cancel = el("button", { class: "raw btn link", type: "button" }, ["Cancel"]);
    cancel.addEventListener("click", () => h.onCancel());
    const sheet = frame("Pay from", el("span", { class: "top-none", aria: { hidden: "true" } }), [
      el("div", { class: "list" }, this.#rows),
      this.#err,
      el("div", { class: "acts" }, [cancel]),
    ]);
    sheet.classList.add("src");
    this.element = sheet;
  }

  /** The bank is answering: no second pick (one request at a time). */
  busy(on: boolean): void {
    this.#busy = on;
    for (const row of this.#rows) row.setAttribute("aria-busy", on ? "true" : "false");
  }

  /** The bank refused this account: its sentence under the list, focus to it, that account resting. */
  refused(message: string, accountId: string): void {
    this.busy(false);
    this.#err.replaceChildren(icon("i-alert"), el("span", {}, [message]));
    this.#err.hidden = false;
    for (const row of this.#rows) row.setAttribute("aria-disabled", row.dataset["account"] === accountId ? "true" : "false");
    this.#err.focus({ preventScroll: true });
  }

  /** Focus to the account it comes from now (the sheet opens on it). */
  focusCurrent(): void {
    (this.#rows.find((r) => r.getAttribute("aria-pressed") === "true") ?? this.#rows[0])?.focus({ preventScroll: true });
  }
}
